import { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { articles, articlesByCategory } from '@/mocks/articles';

const articleImages: Record<string, string> = {
  'article-ssis-001': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20editorial%20composition%20with%20warm%20cream%20and%20terracotta%20tones%2C%20soft%20geometric%20shapes%2C%20clean%20negative%20space%2C%20elegant%20gradient%2C%20modern%20magazine%20style%2C%20warm%20atmosphere%2C%20no%20text%2C%20high%20quality&width=800&height=480&seq=article-ssis-001&orientation=landscape',
  'article-midv-001': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20editorial%20composition%20with%20warm%20cream%20and%20soft%20peach%20tones%2C%20smooth%20geometric%20layers%2C%20clean%20negative%20space%2C%20elegant%20gradient%2C%20modern%20magazine%20style%2C%20warm%20atmosphere%2C%20no%20text%2C%20high%20quality&width=800&height=480&seq=article-midv-001&orientation=landscape',
  'article-top20': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20editorial%20composition%20with%20warm%20beige%20and%20coral%20tones%2C%20layered%20circular%20shapes%2C%20clean%20negative%20space%2C%20elegant%20gradient%2C%20modern%20magazine%20style%2C%20warm%20atmosphere%2C%20no%20text%2C%20high%20quality&width=800&height=480&seq=article-top20&orientation=landscape',
  'article-ipx': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20editorial%20composition%20with%20warm%20cream%20and%20sand%20tones%2C%20elegant%20flowing%20lines%2C%20clean%20negative%20space%2C%20soft%20gradient%2C%20modern%20magazine%20style%2C%20warm%20atmosphere%2C%20no%20text%2C%20high%20quality&width=800&height=480&seq=article-ipx&orientation=landscape',
  'article-search-tips': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20editorial%20composition%20with%20warm%20terracotta%20and%20ivory%20tones%2C%20angular%20geometric%20patterns%2C%20clean%20negative%20space%2C%20elegant%20gradient%2C%20modern%20magazine%20style%2C%20warm%20atmosphere%2C%20no%20text%2C%20high%20quality&width=800&height=480&seq=article-search-tips&orientation=landscape',
  'article-summer-new': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20editorial%20composition%20with%20warm%20peach%20and%20cream%20tones%2C%20organic%20curved%20shapes%2C%20clean%20negative%20space%2C%20elegant%20gradient%2C%20modern%20magazine%20style%2C%20warm%20atmosphere%2C%20no%20text%2C%20high%20quality&width=800&height=480&seq=article-summer-new&orientation=landscape',
  'article-4k': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20editorial%20composition%20with%20warm%20coral%20and%20beige%20tones%2C%20sharp%20geometric%20diamonds%2C%20clean%20negative%20space%2C%20elegant%20gradient%2C%20modern%20magazine%20style%2C%20warm%20atmosphere%2C%20no%20text%2C%20high%20quality&width=800&height=480&seq=article-4k&orientation=landscape',
  'article-collection': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20editorial%20composition%20with%20warm%20sand%20and%20terracotta%20tones%2C%20grid%20based%20shapes%2C%20clean%20negative%20space%2C%20elegant%20gradient%2C%20modern%20magazine%20style%2C%20warm%20atmosphere%2C%20no%20text%2C%20high%20quality&width=800&height=480&seq=article-collection&orientation=landscape',
  'article-abw': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20editorial%20composition%20with%20warm%20ivory%20and%20peach%20tones%2C%20concentric%20geometric%20rings%2C%20clean%20negative%20space%2C%20elegant%20gradient%2C%20modern%20magazine%20style%2C%20warm%20atmosphere%2C%20no%20text%2C%20high%20quality&width=800&height=480&seq=article-abw&orientation=landscape',
  'article-stars': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20editorial%20composition%20with%20warm%20cream%20and%20coral%20tones%2C%20starburst%20geometric%20shapes%2C%20clean%20negative%20space%2C%20elegant%20gradient%2C%20modern%20magazine%20style%2C%20warm%20atmosphere%2C%20no%20text%2C%20high%20quality&width=800&height=480&seq=article-stars&orientation=landscape',
};

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const [activeSlug, setActiveSlug] = useState(slug || 'quanxin');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSlug]);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug]
  );

  const categoryArticles = useMemo(() => {
    const ids = articlesByCategory[activeSlug] || [];
    return articles.filter((a) => ids.includes(a.id));
  }, [activeSlug]);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <section className="w-full bg-background-100 border-b border-background-200/60">
          <div className="w-full px-4 md:px-6 py-8">
            <div className="max-w-6xl mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
                  {t('navHome')}
                </Link>
                <span>/</span>
                <span className="text-foreground-700">{currentCategory?.name || t('navCategories')}</span>
              </nav>

              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
                {currentCategory?.name}
              </h1>
              <p className="text-sm text-foreground-500">
                {currentCategory?.description}
              </p>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 border-b border-background-200/60 overflow-x-auto">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-2 py-4 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveSlug(cat.slug)}
                    className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 hover:text-foreground-900 hover:bg-background-200/50 border border-background-200/60'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative w-full h-48 overflow-hidden">
                        <img
                          src={articleImages[article.imageSeq]}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="text-xs bg-primary-500 text-background-50 px-2.5 py-1 rounded-full whitespace-nowrap">
                            {article.categoryName}
                          </span>
                        </div>
                      </div>
                      <div className="p-5">
                        <h3 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-500 line-clamp-2 mb-4 leading-relaxed">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 text-xs text-foreground-400">
                            <span className="flex items-center gap-1">
                              <div className="w-3.5 h-3.5 flex items-center justify-center">
                                <i className="ri-calendar-line text-xs"></i>
                              </div>
                              {article.date}
                            </span>
                            <span className="flex items-center gap-1">
                              <div className="w-3.5 h-3.5 flex items-center justify-center">
                                <i className="ri-eye-line text-xs"></i>
                              </div>
                              {article.views.toLocaleString()}
                            </span>
                          </div>
                          <span className="text-xs text-foreground-400">{article.readTime}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
                    <i className="ri-inbox-line text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类暂无文章，请浏览其他分类</p>
                  <Link
                    to="/category/quanxin"
                    className="inline-flex items-center gap-1.5 mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors"
                  >
                    浏览全新番号
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-sm"></i>
                    </div>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}